import {
  KARO_TWO,
  SUITS,
  byRankAsc,
  cardsOfSuit,
  dealHands,
  groupBySuit,
  rankOf,
  sortHand,
  suitOf,
  type CardId,
  type DealtCards,
  type Suit,
} from './cards.js';
import {
  DEFAULT_CONFIG,
  IllegalMoveError,
  KITTY_SIZE,
  SEATS,
  TRICKS_PER_DEAL,
  targetOf,
  type Deal,
  type Exchange,
  type ExchangeStep,
  type Match,
  type MatchConfig,
  type Move,
  type Seat,
  type Trick,
  type TrickPlay,
} from './types.js';

/* ------------------------------------------------------------------ *
 * Kopyalama
 *
 * `structuredClone` yerine hedefe özel: bot simülasyonu bunu yüz binlerce
 * kez çağırıyor ve kopyalanacak her alan burada zaten biliniyor. `history`
 * girdileri oluşturulduktan sonra hiç değiştirilmediği için sığ kopya yeter.
 * ------------------------------------------------------------------ */

function cloneDeal(d: Deal): Deal {
  return {
    hands: d.hands.map((h) => [...h]),
    kitty: [...d.kitty],
    buried: [...d.buried],
    trump: d.trump,
    role8: d.role8,
    turn: d.turn,
    trick: { leader: d.trick.leader, plays: d.trick.plays.map((p) => ({ ...p })) },
    tricks: [...d.tricks],
    trickCount: d.trickCount,
    played: [...d.played],
    history: d.history.slice(),
  };
}

function clone(m: Match): Match {
  return {
    config: { ...m.config },
    seed: m.seed,
    phase: m.phase,
    dealIndex: m.dealIndex,
    scores: [...m.scores],
    firstRole8: m.firstRole8,
    deal: m.deal ? cloneDeal(m.deal) : null,
    exchange: m.exchange
      ? {
          queue: m.exchange.queue.map((s) => ({
            from: s.from,
            gives: s.gives.map((g) => ({ ...g })),
          })),
          done: m.exchange.done.map((t) => ({
            ...t,
            given: [...t.given],
            received: [...t.received],
          })),
        }
      : null,
    dealResults: m.dealResults.map((r) => ({
      ...r,
      tricks: [...r.tricks],
      deltas: [...r.deltas],
      scores: [...r.scores],
    })),
    lastDeal: m.lastDeal ? cloneDeal(m.lastDeal) : null,
  };
}

/* ------------------------------------------------------------------ *
 * Roller
 * ------------------------------------------------------------------ */

/** Bu elin 8'cisi. Ayrıca saklanmıyor — maçın ilk 8'cisinden türüyor. */
export const role8Of = (match: Match, dealIndex: number): Seat =>
  ((match.firstRole8 + dealIndex) % 3) as Seat;

export const currentSet = (match: Match): number =>
  Math.floor(match.dealIndex / match.config.dealsPerSet) + 1;

/**
 * İçinde bulunulan setin puanları.
 *
 * Alışveriş önceliği buna bakıyor ("o setin puanı yüksek olan önce ister"),
 * puan çizelgesi de set toplamlarını buradan gösteriyor.
 */
export function setScores(match: Match, setIndex = Math.floor(match.dealIndex / match.config.dealsPerSet)): number[] {
  const start = setIndex * match.config.dealsPerSet;
  const end = Math.min(start + match.config.dealsPerSet, match.dealResults.length);
  const totals = [0, 0, 0];
  for (let i = start; i < end; i++) {
    const result = match.dealResults[i]!;
    for (const s of SEATS) totals[s]! += result.deltas[s]!;
  }
  return totals;
}

/**
 * Maçın ilk 8'cisi: karo 2 kimdeyse o.
 *
 * Karo 2 YERDEKİ 4 karta düşerse (dağıtımların ~%8'i) kimsenin elinde
 * olmuyor. O durumda kural en yakın karşılığına iniyor: elinde EN KÜÇÜK KARO
 * bulunan oyuncu 8'ci oluyor. Yeniden karmak yerine bunu seçtik — masada
 * görünmeyen bir sebeple destenin yeniden dağıtılması oyuncuya açıklanamıyor,
 * "karo 2 yerde kaldı, en küçük karo sende" ise tek cümlede anlaşılıyor.
 */
export function firstRole8From(hands: readonly CardId[][]): Seat {
  const holder = SEATS.find((s) => hands[s]!.includes(KARO_TWO));
  if (holder !== undefined) return holder;

  let best: { seat: Seat; rank: number } | null = null;
  for (const s of SEATS) {
    const diamonds = cardsOfSuit(hands[s]!, 'D');
    const low = diamonds[0];
    if (low === undefined) continue;
    if (best === null || rankOf(low) < best.rank) best = { seat: s, rank: rankOf(low) };
  }
  return best?.seat ?? 0;
}

/* ------------------------------------------------------------------ *
 * Maç kurulumu
 * ------------------------------------------------------------------ */

export function createMatch(seed: string, config: Partial<MatchConfig> = {}): Match {
  const match: Match = {
    config: { ...DEFAULT_CONFIG, ...config },
    seed,
    phase: 'trump',
    dealIndex: 0,
    scores: [0, 0, 0],
    firstRole8: 0,
    deal: null,
    exchange: null,
    dealResults: [],
    lastDeal: null,
  };

  // İlk dağıtım 8'ciyi de belirlediği için önce kartlar, sonra rol.
  const dealt = dealHands(`${seed}#0`);
  match.firstRole8 = firstRole8From(dealt.hands);
  startDeal(match, dealt);
  return match;
}

function startDeal(match: Match, dealt?: DealtCards): void {
  const cards = dealt ?? dealHands(`${match.seed}#${match.dealIndex}`);
  const role8 = role8Of(match, match.dealIndex);

  match.deal = {
    hands: cards.hands.map((h) => sortHand(h)),
    kitty: [...cards.kitty],
    buried: [],
    trump: null,
    role8,
    // 8'ci hem kozu söyleyip gömüyor hem de ilk turu açıyor.
    turn: role8,
    trick: { leader: role8, plays: [] },
    tricks: [0, 0, 0],
    trickCount: 0,
    played: [],
    history: [],
  };
  match.phase = 'trump';
  match.exchange = null;
}

/** Elle kurulmuş bir el — test, replay ve bulmaca senaryoları için. */
export function matchFromHands(
  hands: CardId[][],
  role8: Seat,
  trump: Suit,
  config: Partial<MatchConfig> = {},
): Match {
  return {
    config: { ...DEFAULT_CONFIG, ...config },
    seed: 'manual',
    phase: 'play',
    dealIndex: 0,
    scores: [0, 0, 0],
    firstRole8: role8,
    deal: {
      hands: hands.map((h) => sortHand(h)),
      kitty: [],
      buried: [],
      trump,
      role8,
      turn: role8,
      trick: { leader: role8, plays: [] },
      tricks: [0, 0, 0],
      trickCount: 0,
      played: [],
      history: [],
    },
    exchange: null,
    dealResults: [],
    lastDeal: null,
  };
}

/* ------------------------------------------------------------------ *
 * Koz seçimi ve gömme — ikisi de 8'ciye ait
 * ------------------------------------------------------------------ */

export function submitTrump(match: Match, seat: Seat, suit: Suit): Match {
  if (match.phase !== 'trump' || !match.deal) {
    throw new IllegalMoveError('Şu anda koz seçilmiyor');
  }
  if (seat !== match.deal.role8) throw new IllegalMoveError('Kozu 8’ci seçer');
  if (!SUITS.includes(suit)) throw new IllegalMoveError('Geçersiz renk');

  const next = clone(match);
  const deal = next.deal!;
  deal.trump = suit;
  next.phase = 'bury';
  return next;
}

/**
 * 8'cinin gömmesi.
 *
 * SIRA ÖNEMLİ: önce 4 kart gömülüyor, SONRA yerdeki 4 kart eline geçiyor.
 * Yani 8'ci neyi aldığını görmeden neyi attığına karar veriyor — ihaleyi
 * kazanmanın riski burada.
 */
export function submitBury(match: Match, seat: Seat, cards: readonly CardId[]): Match {
  if (match.phase !== 'bury' || !match.deal) {
    throw new IllegalMoveError('Şu anda kart gömülmüyor');
  }
  if (seat !== match.deal.role8) throw new IllegalMoveError('Kartı 8’ci gömer');
  if (cards.length !== KITTY_SIZE) {
    throw new IllegalMoveError(`${KITTY_SIZE} kart gömmelisin`);
  }
  if (new Set(cards).size !== cards.length) {
    throw new IllegalMoveError('Aynı kart birden fazla kez gömülemez');
  }
  const hand = new Set(match.deal.hands[seat]!);
  if (!cards.every((c) => hand.has(c))) throw new IllegalMoveError('Bu kartlar elinde yok');

  const next = clone(match);
  const deal = next.deal!;
  const buried = new Set(cards);
  deal.hands[seat] = sortHand([
    ...deal.hands[seat]!.filter((c) => !buried.has(c)),
    ...deal.kitty,
  ]);
  deal.buried = [...cards];
  deal.kitty = [];
  next.phase = 'play';
  return next;
}

/** Süre aşımı / bot: kozdan olmayan en düşük 4 kartı gömer. */
export function autoBury(match: Match, seat: Seat): Match {
  const deal = match.deal!;
  const hand = deal.hands[seat]!;
  const offTrump = hand.filter((c) => suitOf(c) !== deal.trump);
  const pool = offTrump.length >= KITTY_SIZE ? offTrump : hand;
  return submitBury(match, seat, byRankAsc(pool).slice(0, KITTY_SIZE));
}

/** Süre aşımı: en uzun rengi koz yapar. */
export function autoTrump(match: Match, seat: Seat): Match {
  const hand = match.deal!.hands[seat]!;
  const groups = groupBySuit(hand);
  let best: Suit = 'S';
  let bestSize = -1;
  for (const suit of SUITS) {
    const size = groups.get(suit)?.length ?? 0;
    if (size > bestSize) {
      bestSize = size;
      best = suit;
    }
  }
  return submitTrump(match, seat, best);
}

/* ------------------------------------------------------------------ *
 * Oyun — geçerli kartlar
 * ------------------------------------------------------------------ */

/**
 * Sıradaki oyuncunun oynayabileceği kartlar.
 *
 * Üç zorunluluk üst üste biniyor:
 *   1. Yerdeki renk elinde varsa o renkten oynamak zorunda,
 *   2. …ve o renkten yerdeki en büyüğü GEÇEBİLİYORSA geçmek zorunda —
 *      ama yalnızca tur hâlâ o renkte çekişiyorsa (aşağıya bak),
 *   3. Yerdeki renk yoksa ve kozu varsa koz atmak zorunda (koz da atılmışsa
 *      onu da geçmek zorunda).
 * Ne renk ne koz kaldıysa el serbest.
 *
 * Koz açmak serbest: "koz kırılmadan koz çıkılmaz" kuralı bu oyunda yok.
 */
export function legalCards(deal: Deal, seat: Seat): CardId[] {
  const hand = deal.hands[seat]!;
  if (deal.turn !== seat || hand.length === 0) return [];

  const plays = deal.trick.plays;
  if (plays.length === 0) return [...hand];

  const trump = deal.trump!;
  const ledSuit = suitOf(plays[0]!.card);
  const own = cardsOfSuit(hand, ledSuit);

  if (own.length > 0) {
    /**
     * ARAYA KOZ GİRDİYSE YÜKSELTME ZORUNLULUĞU DÜŞER.
     *
     * Tur artık kozun: yerdeki rengin en büyüğünü geçmek turu kazandırmıyor,
     * yalnızca oyuncunun elindeki iyi kartı boşa harcatıyor. Zorunluluğun
     * amacı turu çekişmeye açık tutmak; çekişme kozla bittiği an amaç da
     * bitiyor. (Koz rengi AÇILDIYSA durum farklı — orada yükseltme hâlâ turu
     * kazanmanın yolu, o yüzden `ledSuit !== trump` şartı var.)
     */
    const trumped = ledSuit !== trump && plays.some((p) => suitOf(p.card) === trump);
    if (trumped) return own;

    const best = Math.max(
      ...plays.filter((p) => suitOf(p.card) === ledSuit).map((p) => rankOf(p.card)),
    );
    const higher = own.filter((c) => rankOf(c) > best);
    return higher.length > 0 ? higher : own;
  }

  const trumps = cardsOfSuit(hand, trump);
  if (trumps.length > 0) {
    const played = plays.filter((p) => suitOf(p.card) === trump).map((p) => rankOf(p.card));
    if (played.length === 0) return trumps;
    const best = Math.max(...played);
    const higher = trumps.filter((c) => rankOf(c) > best);
    return higher.length > 0 ? higher : trumps;
  }

  return [...hand];
}

/** Turu kim aldı — koz varsa en yüksek koz, yoksa yerdeki rengin en yükseği. */
export function trickWinner(plays: readonly TrickPlay[], trump: Suit): Seat {
  const ledSuit = suitOf(plays[0]!.card);
  const trumped = plays.filter((p) => suitOf(p.card) === trump);
  const pool = trumped.length > 0 ? trumped : plays.filter((p) => suitOf(p.card) === ledSuit);
  return pool.reduce((a, b) => (rankOf(a.card) >= rankOf(b.card) ? a : b)).seat;
}

/** Tur şu an kimde — henüz kapanmamış turda "önde giden". */
export function trickHolder(trick: Trick, trump: Suit): Seat | null {
  return trick.plays.length === 0 ? null : trickWinner(trick.plays, trump);
}

/** Hamle geçerli mi? Geçersizse Türkçe sebep, geçerliyse null döner. */
export function checkMove(match: Match, seat: Seat, move: Move): string | null {
  if (match.phase === 'exchange') return 'Kart alışverişi bitmeden oynanamaz';
  if (match.phase === 'trump') return 'Önce koz seçilmeli';
  if (match.phase === 'bury') return 'Önce kartlar gömülmeli';
  if (match.phase !== 'play' || !match.deal) return 'Maç bitti';

  const deal = match.deal;
  if (deal.turn !== seat) return 'Sıra sende değil';
  if (!deal.hands[seat]!.includes(move.card)) return 'Bu kart elinde yok';
  if (legalCards(deal, seat).includes(move.card)) return null;

  // Neden geçersiz olduğunu tam söylüyoruz: "geçersiz hamle" oyuncuya kuralı
  // öğretmiyor, hangi zorunluluğa takıldığı öğretiyor.
  const trump = deal.trump!;
  const ledSuit = suitOf(deal.trick.plays[0]!.card);
  const own = cardsOfSuit(deal.hands[seat]!, ledSuit);
  if (own.length > 0) {
    return suitOf(move.card) === ledSuit
      ? 'Yerdeki en büyüğü geçmelisin'
      : `Elinde ${ledSuit === trump ? 'koz' : 'yerdeki renkten'} kart var, onu oynamalısın`;
  }
  return suitOf(move.card) === trump ? 'Yerdeki kozu geçmelisin' : 'Koz atmalısın';
}

export const isLegal = (match: Match, seat: Seat, move: Move): boolean =>
  checkMove(match, seat, move) === null;

/* ------------------------------------------------------------------ *
 * Hamle uygulama
 * ------------------------------------------------------------------ */

/** Hamleyi uygular ve YENİ bir Match döner (girdi değiştirilmez). */
export function applyMove(match: Match, seat: Seat, move: Move): Match {
  const error = checkMove(match, seat, move);
  if (error) throw new IllegalMoveError(error);

  const next = clone(match);
  const deal = next.deal!;

  deal.hands[seat] = deal.hands[seat]!.filter((c) => c !== move.card);
  deal.trick.plays.push({ seat, card: move.card });
  deal.played.push(move.card);
  deal.history.push({ kind: 'play', seat, card: move.card });

  if (deal.trick.plays.length < SEATS.length) {
    deal.turn = ((seat + 1) % 3) as Seat;
    return next;
  }

  const winner = trickWinner(deal.trick.plays, deal.trump!);
  deal.tricks[winner]! += 1;
  deal.trickCount += 1;
  deal.history.push({ kind: 'trickEnd', winner, tricks: [...deal.tricks] });
  deal.trick = { leader: winner, plays: [] };
  deal.turn = winner;

  if (deal.trickCount >= TRICKS_PER_DEAL) endDeal(next);
  return next;
}

/** Süresi dolan oyuncunun otomatik hamlesi: en düşük geçerli kart. */
export function timeoutMove(match: Match, seat: Seat): Move {
  const legal = legalCards(match.deal!, seat);
  return { type: 'play', card: byRankAsc(legal)[0]! };
}

/* ------------------------------------------------------------------ *
 * El sonu
 * ------------------------------------------------------------------ */

function endDeal(match: Match): void {
  const deal = match.deal!;
  const deltas = SEATS.map((s) => deal.tricks[s]! - targetOf(deal.role8, s));

  for (const s of SEATS) match.scores[s]! += deltas[s]!;
  match.dealResults.push({
    role8: deal.role8,
    trump: deal.trump!,
    tricks: [...deal.tricks],
    deltas,
    scores: [...match.scores],
  });
  match.lastDeal = deal;
  match.dealIndex += 1;

  if (match.dealIndex >= match.config.dealsPerSet * match.config.sets) {
    match.phase = 'matchOver';
    return;
  }

  startDeal(match);

  /**
   * SET BAŞINDA ALIŞVERİŞ YOK — her set temiz sayfa açıyor. 4 ellik bir sette
   * 3 alışveriş oluyor (2., 3. ve 4. elin öncesinde), maç boyunca toplam 9.
   */
  if (match.dealIndex % match.config.dealsPerSet === 0) return;

  const exchange = buildExchange(match, deltas);
  if (exchange) {
    match.exchange = exchange;
    match.phase = 'exchange';
  }
}

/* ------------------------------------------------------------------ *
 * El sonu kart alışverişi
 * ------------------------------------------------------------------ */

/**
 * Kimin kimden kaç kart isteyeceğini kurar.
 *
 * Miktarlarda hiç belirsizlik yok: hedefler 8+5+3 = 16 el olduğu için sapmalar
 * her zaman sıfır toplamlı, yani artı toplamı eksi toplamına eşit. Üç oyuncuda
 * ya bir isteyen iki verene ya iki isteyen bir verene düşüyor; her iki halde de
 * dağılım tek türlü.
 *
 * SIRA önemli, çünkü ilk isteyen karşı tarafın elini değiştiriyor: önce o
 * setin puanı yüksek olan, eşitlikte OYNANACAK elin rolü büyük olan (8 > 5 > 3).
 */
function buildExchange(match: Match, deltas: readonly number[]): Exchange | null {
  const takers = SEATS.filter((s) => deltas[s]! > 0);
  const givers = SEATS.filter((s) => deltas[s]! < 0);
  if (takers.length === 0 || givers.length === 0) return null;

  const role8 = role8Of(match, match.dealIndex);
  const points = setScores(match, Math.floor((match.dealIndex - 1) / match.config.dealsPerSet));
  const priority = [...takers].sort(
    (a, b) => points[b]! - points[a]! || targetOf(role8, b) - targetOf(role8, a),
  );

  const owed = new Map<Seat, number>(givers.map((s) => [s, -deltas[s]!]));
  const queue: ExchangeStep[] = [];

  for (const from of priority) {
    let left = deltas[from]!;
    const gives: ExchangeStep['gives'] = [];
    for (const to of givers) {
      if (left <= 0) break;
      const count = Math.min(left, owed.get(to) ?? 0);
      if (count <= 0) continue;
      gives.push({ to, count });
      owed.set(to, (owed.get(to) ?? 0) - count);
      left -= count;
    }
    if (gives.length > 0) queue.push({ from, gives });
  }

  return queue.length > 0 ? { queue, done: [] } : null;
}

/**
 * Zorunlu karşılık: verilen her kart için karşı tarafın vereceği kart.
 *
 * Kural üç dala ayrılıyor — hepsi kartın TİPİ (rengi) üzerinden:
 *   • o renkten hiç kartı yok      → geleni aynen geri verir
 *   • en yükseği gelenden düşük    → o renkten EN DÜŞÜĞÜNÜ verir
 *   • diğer hâllerde               → o renkten EN YÜKSEĞİNİ verir
 *
 * Aynı renkten iki kart gelirse sıra büyükten küçüğe işleniyor; normal
 * durumda o rengin en yüksek iki kartı gidiyor.
 *
 * `hand` KARŞI TARAFIN ELİ — gelen kartlar havuza katılmıyor. Katılsaydı,
 * eli boş olan oyuncunun "geleni geri ver" hâli, gelen kartı kendi en yükseği
 * sayıp aynı kartı zorunlu kılardı; iki dal birbirine karışırdı.
 */
export function forcedReturn(hand: readonly CardId[], given: readonly CardId[]): CardId[] {
  const pool = [...hand];
  const out: CardId[] = [];

  for (const card of [...given].sort((a, b) => rankOf(b) - rankOf(a))) {
    const same = cardsOfSuit(pool, suitOf(card));
    if (same.length === 0) {
      out.push(card);
      continue;
    }
    const highest = same[same.length - 1]!;
    const pick = rankOf(highest) < rankOf(card) ? same[0]! : highest;
    pool.splice(pool.indexOf(pick), 1);
    out.push(pick);
  }

  return out;
}

/** Alışverişte verilen kartlar, işlenme sırasıyla (büyükten küçüğe). */
const orderGiven = (cards: readonly CardId[]): CardId[] =>
  [...cards].sort((a, b) => rankOf(b) - rankOf(a));

/**
 * Sıradaki isteyenin seçimi. İki verene birden veriyorsa ikisi de tek
 * seferde bildiriliyor — dönen kartları aynı anda görüyor.
 */
export function submitExchange(
  match: Match,
  seat: Seat,
  gives: readonly { to: Seat; cards: CardId[] }[],
): Match {
  if (match.phase !== 'exchange' || !match.exchange || !match.deal) {
    throw new IllegalMoveError('Şu anda kart alışverişi yapılmıyor');
  }
  const step = match.exchange.queue[0];
  if (!step) throw new IllegalMoveError('Bekleyen alışveriş yok');
  if (step.from !== seat) throw new IllegalMoveError('Sıra sende değil');

  // Hedefler ve adetler bire bir tutmalı; eksik/fazla/yanlış hedef kabul yok.
  if (gives.length !== step.gives.length) {
    throw new IllegalMoveError('Eksik ya da fazla hedef');
  }
  for (const want of step.gives) {
    const got = gives.find((g) => g.to === want.to);
    if (!got) throw new IllegalMoveError('Beklenen oyuncu listede yok');
    if (got.cards.length !== want.count) {
      throw new IllegalMoveError(`${want.count} kart vermelisin`);
    }
  }

  const all = gives.flatMap((g) => g.cards);
  if (new Set(all).size !== all.length) {
    throw new IllegalMoveError('Aynı kart birden fazla kez verilemez');
  }
  const hand = new Set(match.deal.hands[seat]!);
  if (!all.every((c) => hand.has(c))) throw new IllegalMoveError('Bu kartlar elinde yok');

  const next = clone(match);
  const deal = next.deal!;

  for (const give of gives) {
    const given = orderGiven(give.cards);
    const received = forcedReturn(deal.hands[give.to]!, given);

    const outSet = new Set(given);
    const inSet = new Set(received);

    // Geri dönen kart, verilen kartın kendisi olabilir (karşı tarafta o renk
    // yoksa). Aşağıdaki iki satır o durumu da doğru veriyor: kart karşıya
    // geçip aynı adımda geri geliyor, net etki sıfır.
    deal.hands[give.to] = sortHand(
      [...deal.hands[give.to]!, ...given].filter((c) => !inSet.has(c)),
    );
    deal.hands[seat] = sortHand([
      ...deal.hands[seat]!.filter((c) => !outSet.has(c)),
      ...received,
    ]);

    next.exchange!.done.push({ from: seat, to: give.to, given, received });
  }

  next.exchange!.queue.shift();
  // Kuyruk boşaldı: sıra koz seçimine geçiyor. `exchange` nesnesi duruyor —
  // el boyunca "ne alıp ne verdin" özetini o taşıyor.
  if (next.exchange!.queue.length === 0) next.phase = 'trump';
  return next;
}

/** Süre aşımı / bot: en düşük kartları verir. */
export function autoExchange(match: Match, seat: Seat): Match {
  const step = match.exchange?.queue[0];
  if (!step || step.from !== seat) throw new IllegalMoveError('Sıra sende değil');

  const pool = byRankAsc(match.deal!.hands[seat]!);
  let cursor = 0;
  const gives = step.gives.map((g) => ({
    to: g.to,
    cards: pool.slice(cursor, (cursor += g.count)),
  }));
  return submitExchange(match, seat, gives);
}

/* ------------------------------------------------------------------ *
 * Skor
 * ------------------------------------------------------------------ */

/** Maç sonu sıralaması. Eşit puanlılar aynı `place` değerini alır. */
export function standings(match: Match): { seat: Seat; score: number; place: number }[] {
  const rows = SEATS.map((seat) => ({ seat, score: match.scores[seat]!, place: 0 }));
  rows.sort((a, b) => b.score - a.score);
  rows.forEach((r, i) => {
    r.place = i > 0 && rows[i - 1]!.score === r.score ? rows[i - 1]!.place : i + 1;
  });
  return rows;
}
